import logger from './logger';
/**
 * Deep-clones an object while removing non-JSON serializable values
 * Drops DOM nodes, React elements, functions, symbols, and other non-JSON data
 * 
 * @param {any} value - The value to sanitize
 * @param {WeakSet} seen - Set to track circular references
 * @returns {any} - Sanitized value safe for JSON serialization
 */
export const toPlainJSON = (value, seen = new WeakSet()) => {
  const t = typeof value;

  // Primitives ok (except functions/symbols/bigints)
  if (value == null || t === 'string' || t === 'number' || t === 'boolean') return value;
  if (t === 'function' || t === 'symbol' || t === 'bigint') return undefined;

  // Guard cycles
  if (t === 'object') {
    if (seen.has(value)) return undefined;
    seen.add(value);

    // Drop DOM nodes
    if (value instanceof Element || value?.nodeType === 1 || value?.ownerDocument) return undefined;

    // Drop React elements
    if (value?.$$typeof) return undefined;

    // Arrays
    if (Array.isArray(value)) {
      const arr = value.map(v => toPlainJSON(v, seen)).filter(v => v !== undefined);
      return arr;
    }

    // Plain object
    const out = {};
    for (const [k, v] of Object.entries(value)) {
      const next = toPlainJSON(v, seen);
      if (next !== undefined) out[k] = next;
    }
    return out;
  }

  // Anything else → drop
  return undefined;
};

/**
 * Sanitizes finalized offers data for JSON serialization
 * Removes React elements, DOM nodes, and other non-JSON data
 * 
 * @param {Array} finalizedOffers - Array of finalized offer objects
 * @returns {Array} - Sanitized array safe for JSON serialization
 */
export const sanitizeFinalizedOffers = (finalizedOffers) => {
  if (!Array.isArray(finalizedOffers)) {
    logger.warn('sanitizeFinalizedOffers: Expected array, got', typeof finalizedOffers);
    return [];
  }

  return finalizedOffers.map(offer => toPlainJSON(offer));
};

/**
 * Sanitizes all rows data for JSON serialization
 * Removes React elements, DOM nodes, and other non-JSON data
 * 
 * @param {Array} allRows - Array of row objects
 * @returns {Array} - Sanitized array safe for JSON serialization
 */
export const sanitizeAllRows = (allRows) => {
  if (!Array.isArray(allRows)) {
    logger.warn('sanitizeAllRows: Expected array, got', typeof allRows);
    return [];
  }

  return allRows.map(row => toPlainJSON(row));
};
